/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import ContextProcessor from '../../../context/context-processor';
import * as RulesStore from '../../../rules/rules-store';
import type { C11nEnv } from '../../../interpreter/c11n-env';
import Utils from '../../../utils/utils';

import Parser from './parser';
import type { AST, GrammarList, Token, States, ExpressionHandlers, IOperatorHandlers, OneParamEvaluate } from './types';

const LITERAL = 'literal';
const IDENTIFIER = 'identifier';

const andGrammar = {
  operatorType: 'binaryOperator',
  precedence: 1,
  evaluate: (left: string, right: string) => left && right
};

const orGrammar = {
  operatorType: 'binaryOperator',
  precedence: 1,
  evaluate: (left: string, right: string) => left || right
};

const compareValues = (
  left: string | boolean | null | undefined | number,
  right: string | boolean | null | undefined | number,
  isEqual: boolean
): boolean => {
  const isLeftNullish = left === '';
  const isRightNullish = right === '' || right === null || right === undefined;
  if (isLeftNullish && isRightNullish) {
    return isEqual;
  }
  if (typeof right === 'boolean') {
    const isTruthyString = !isLeftNullish && left !== 'false' && left !== '0';
    const leftBool = isTruthyString ? Boolean(left) : false;
    return isEqual ? leftBool === right : leftBool !== right;
  }
  if (isLeftNullish !== isRightNullish) {
    return !isEqual;
  }
  return isEqual ? left === right : left !== right;
};

/**
 * A map of all expression elements to their properties. Note that
 * changes here may require changes in the Lexer and Parser.
 */
export const supportedGrammar: GrammarList = {
  '>': {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => left > right
  },
  '>=': {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => left >= right
  },
  '<': {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => left < right
  },
  '<=': {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => left <= right
  },
  '==': {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => compareValues(left, right, true)
  },
  '!=': {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => compareValues(left, right, false)
  },
  '!': {
    operatorType: 'unaryOperator',
    precedence: 999,
    evaluate: (right: AST | string): boolean => {
      let { value } = right as AST;
      if (value === 'true' || value === 'false') {
        value = value === 'true';
      }
      return !value;
    }
  },
  '(': { operatorType: 'openParenthesis' },
  ')': { operatorType: 'closeParenthesis' },
  '&&': andGrammar,
  AND: andGrammar,
  and: andGrammar,
  '||': orGrammar,
  OR: orGrammar,
  or: orGrammar,
  CONTAINS: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      return left.includes(right);
    }
  },
  NOT_CONTAINS: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      return !left.includes(right);
    }
  },
  STARTS_WITH: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      return left.startsWith(right);
    }
  },
  NOT_STARTS_WITH: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      return !left.startsWith(right);
    }
  },
  ENDS_WITH: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      return left.endsWith(right);
    }
  },
  NOT_ENDS_WITH: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      return !left.endsWith(right);
    }
  },
  IS_NULL: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (value: string) => {
      return !value;
    }
  },
  IS_NOT_NULL: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (value: string) => {
      return !!value;
    }
  },
  IS_IN_LIST: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      const list = right.split(',');
      return list.includes(left);
    }
  },
  IS_NOT_IN_LIST: {
    operatorType: 'binaryOperator',
    precedence: 2,
    evaluate: (left: string, right: string) => {
      const list = right.split(',');
      return !list.includes(left);
    }
  }
};

/**
 * Expression handlers. Handles the different types of expression.
 */
const expressionHandlers: ExpressionHandlers = {
  /**
   * [BinaryExpression]
   * Description : Evaluates a binary expression.
   * @param ast     Abstract syntax tree
   * @returns true if the expression evaluates to true else false.
   */
  BinaryExpression: function BinaryExpression(ast: AST): boolean {
    const result: (string | number | boolean)[] = [];
    const right = ast.right;
    const operator = ast.operator as string;
    const left = ast.left as AST;
    let leftType = typeof left.value === 'boolean' ? 'True-False' : '';
    result[0] = this.evaluateAST(left);
    if (!leftType && left.operationType === IDENTIFIER && typeof left.value === 'string') {
      const propertyName = left.value.substring(1);
      leftType = this._context?.getFieldMetadata(propertyName)?.type ?? '';
      const { pageReference, context } = this._contextTreeNodeContext ?? {};
      if (!leftType && pageReference && context) {
        const classID = ContextProcessor.resolveClassID(left.value, pageReference, context);
        const propMetadata = RulesStore.getPropertyMetadata(propertyName, classID);
        if (propMetadata && !Array.isArray(propMetadata)) {
          leftType = propMetadata.type ?? '';
        }
      }
    }
    result[1] = right ? this.evaluateAST(right, leftType) : '';
    const evaluate = this._grammar[operator].evaluate as Function;
    return evaluate(result[0] as string, result[1] as string);
  },

  /**
   * [identifier]
   * Description : Returns the identifier value from the context.
   * @param ast     Abstract syntax tree
   * @returns identifier value from the context
   */
  identifier: function identifier(ast: AST): boolean | string | number {
    if (this._localDataObject) {
      return Utils.getIn(this._localDataObject, ast.value ? (ast.value as string).substring(1).split('.') : []) ?? '';
    }
    if (this._contextTreeNodeContext) {
      return ContextProcessor.getLitePropertyValue(this._contextTreeNodeContext, ast.value as string) ?? '';
    }
    return (this._context as C11nEnv).getValue(ast.value as string) ?? '';
  },

  /**
   * [literal]
   * Description : Returns the literal value.
   * @param ast     Abstract syntax tree
   * @returns Literal value.
   */
  literal: function literal(ast: AST, leftType?: string): boolean | string | number {
    const hasleftType = leftType === undefined || leftType === '' || leftType === 'True-False';
    if (hasleftType && (ast.value === 'true' || ast.value === 'false')) {
      return ast.value === 'true';
    }
    return ast.value as boolean | string | number;
  },

  /**
   * [UnaryExpression]
   * Description : Evaluates a unary expression.
   * @param ast     Abstract syntax tree
   * @returns true if the expression evaluates to true else false.
   */
  UnaryExpression: function UnaryExpression(ast: AST): boolean | string | number {
    const operator = ast.operator as string;
    const right = ast.right as AST;
    const evaluate = this._grammar[operator].evaluate as OneParamEvaluate;
    return evaluate(right);
  }
};
/**
 * Handlers for all the types of operators that are supported.
 */
const OperatorHandlers: IOperatorHandlers = {
  /**
   * [unaryOperator]
   * Description : Handles the unary operator.
   * @param token       Current token
   */
  unaryOperator: function unaryOperator(token: Token) {
    this.setPositionInAST({
      operationType: 'UnaryExpression',
      operator: token.value as string
    });
  },

  /**
   * [binaryOperator]
   * Description : Handles the binary operator token.
   * @param token       Current token
   */
  binaryOperator: function binaryOperator(token: Token) {
    const { precedence } = this._grammar[token.value as string];
    let parent = this._position._parent;
    const operator = parent ? parent.operator : null;
    const grammer = operator ? this._grammar[operator] : null;
    const grammerPrecedence = grammer ? grammer.precedence : null;
    if (grammerPrecedence && precedence) {
      while (parent?.operator && grammerPrecedence >= precedence) {
        this._position = parent;
        parent = parent._parent;
      }
    }

    const node: AST = {
      operationType: 'BinaryExpression',
      operator: token.value as string,
      left: this._position
    };

    Parser._setParent(this._position, node);
    this._position = parent as AST;
    this.setPositionInAST(node);
  },

  /**
   * [identifier]
   * Description : Handles the identifier token.
   * @param token       Current token
   */
  identifier: function identifier(token: Token) {
    const node: AST = {
      operationType: IDENTIFIER,
      value: token.value as string
    };
    this.setPositionInAST(node);
  },

  /**
   * [literal]
   * Description : Handles the literal token.
   * @param token       Current token
   */
  literal: function literal(token: Token) {
    this.setPositionInAST({
      operationType: LITERAL,
      value: token.value as string
    });
  },

  /**
   * [subExpression]
   * Description : Handles the subExpression token.
   * @param ast       Abstract syntax tree
   */
  subExpression: function subExpression(ast: Token) {
    this.setPositionInAST(ast as unknown as AST);
  }
};

/**
 * States in the evaluation of an expression.
 */
export const states: States = {
  expectOperand: {
    operatorTypes: {
      literal: { toState: 'expectBinOp' },
      identifier: { toState: IDENTIFIER },
      unaryOperator: {},
      openParenthesis: { toState: 'subExpression' }
    }
  },
  expectBinOp: {
    operatorTypes: {
      binaryOperator: { toState: 'expectOperand' }
    }
  },
  identifier: {
    operatorTypes: {
      binaryOperator: { toState: 'expectOperand' }
    }
  },
  subExpression: {
    subHandler: OperatorHandlers.subExpression,
    endStates: {
      closeParenthesis: 'expectBinOp'
    }
  }
};

export { OperatorHandlers, expressionHandlers };
